import React, { useState, useRef } from "react";
import * as MD from "reports/measures/MeasureDef";

interface AssessmentDeterminationPillProps {
    measureType: MD.MeasureType;
    decisions: any[]; // AssessmentDecision[] - using any to avoid circular dependency
}

const AssessmentDeterminationPill: React.FC<AssessmentDeterminationPillProps> = ({ measureType, decisions }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<'left' | 'right'>('right');
    const pillRef = useRef<HTMLSpanElement>(null);

    if (!decisions || decisions.length === 0) {
        return null;
    }

    const decision = decisions.find(d => d.trajectory?.measureType === measureType);

    if (!decision) {
        return null;
    }

    const getCategoryBadgeClass = (category: string) => {
        switch (category) {
            case "physical":
                return "bg-blue-100 text-blue-800";
            case "behavioral":
                return "bg-purple-100 text-purple-800";
            case "social":
                return "bg-teal-100 text-teal-800";
            case "continuedNeeds":
                return "bg-orange-100 text-orange-800";
            case "excluded":
                return "bg-gray-100 text-gray-600";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getCategoryLabel = (category: string) => {
        switch (category) {
            case "physical":
                return "Physical";
            case "behavioral":
                return "Behavioral";
            case "social":
                return "Social";
            case "continuedNeeds":
                return "Continued Needs";
            case "excluded":
                return "Excluded";
            default:
                return category;
        }
    };

    const handleMouseEnter = () => {
        if (pillRef.current) {
            const rect = pillRef.current.getBoundingClientRect();
            const spaceOnRight = window.innerWidth - rect.right;
            // If less than 350px on right, show tooltip on left
            setTooltipPosition(spaceOnRight < 350 ? 'left' : 'right');
        }
        setShowTooltip(true);
    };

    return (
        <span
            ref={pillRef}
            className={`relative inline-block px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getCategoryBadgeClass(decision.category)}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => setShowTooltip(false)}
        >
            {getCategoryLabel(decision.category)}
            {showTooltip && (
                <span
                    className={`absolute z-[100] px-1 py-0.5 text-xs text-white bg-black/70 rounded whitespace-normal max-w-xs shadow-lg ${
                        tooltipPosition === 'right' ? 'left-full ml-2' : 'right-full mr-2'
                    } top-1/2 -translate-y-1/2`}
                    style={{ minWidth: '220px' }}
                >
                    {decision.reason}
                </span>
            )}
        </span>
    );
};

export default AssessmentDeterminationPill;
